import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Calendar from "react-calendar";
import { useNavigate } from "react-router-dom";
import "react-calendar/dist/Calendar.css";
import api, { getCalendarSetup } from "../api/api";
import "../style/index.css";

import Header from "../components/Header";
import LeftSidebar from "../components/sidebar";

const MAX_LANES = 3;
const BAR_HEIGHT = 16;
const BAR_GAP = 3;

const toDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const normalizeDate = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const normalizeDateFromString = (value, timezone) => {
  if (!value) return null;
  if (value instanceof Date) return normalizeDate(value);
  const str = String(value);
  // If already a date-only string, use it directly.
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    return normalizeDate(`${str}T00:00:00`);
  }
  const isNaiveIso =
    typeof str === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(str);
  const date = new Date(isNaiveIso ? `${str}Z` : str);
  const tz = timezone || "UTC";
  const dateOnly = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
  return normalizeDate(`${dateOnly}T00:00:00`);
};

const startOfWeek = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  return d;
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const getBarSegments = (event, weekStart, weekEnd) => {
  const eStart = event.startDate;
  const eEnd = event.endDate;

  const segStart = eStart < weekStart ? weekStart : eStart;
  const segEnd = eEnd > weekEnd ? weekEnd : eEnd;

  if (segStart > segEnd) return null;

  const col0 = segStart.getDay();
  const col1 = segEnd.getDay();
  const spanCols = col1 - col0 + 1;

  const isFirstSeg = isSameDay(eStart, segStart);
  const isLastSeg = isSameDay(eEnd, segEnd);

  return {
    col0,
    spanCols,
    segStart,
    segEnd,
    showLabel: isFirstSeg || segStart.getDay() === 0,
    shape: isFirstSeg && isLastSeg ? "solo" : isFirstSeg ? "start" : isLastSeg ? "end" : "mid",
    isContinuation: eStart < weekStart,
  };
};

const formatLongDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(`${dateStr}T00:00:00`);
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
};

const formatSpanBadge = (startStr, endStr) => {
  if (!startStr || !endStr) return "";
  const start = new Date(startStr + "T00:00:00");
  const end = new Date(endStr + "T00:00:00");
  const days = Math.floor((end - start) / (24 * 60 * 60 * 1000)) + 1;
  return `Spans ${startStr}  ${endStr}  ${days} days`;
};

const formatDateTimeShort = (dateStr) => {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr + "T00:00:00");
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

const formatDateTimeWithTz = (dateStr, timezone) => {
  if (!dateStr) return "N/A";
  try {
    const isNaiveIso =
      typeof dateStr === "string" &&
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(dateStr);
    const date = new Date(isNaiveIso ? `${dateStr}Z` : dateStr);
    const formatted = date.toLocaleString("en-US", {
      timeZone: timezone || "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).replace(",", "");
    return timezone ? `${formatted} (${timezone})` : formatted;
  } catch {
    return dateStr;
  }
};

const formatDateTime = (datetime, timezone) => {
  if (!datetime) return "N/A";
  try {
    return new Date(datetime)
      .toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
      .replace(",", "");
  } catch {
    return String(datetime);
  }
};

const countDaysBetween = (startStr, endStr) => {
  if (!startStr || !endStr) return 0;
  const start = new Date(startStr + "T00:00:00");
  const end = new Date(endStr + "T00:00:00");
  const days = Math.floor((end - start) / (24 * 60 * 60 * 1000)) + 1;
  return isNaN(days) ? 0 : days;
};

const extractCitiesFromLegs = (legs = []) => {
  const cities = new Set();
  legs.forEach((leg) => {
    const route = leg.route || "";
    const parts = route.split("→").map((p) => p.trim());
    if (parts[0]) cities.add(parts[0]);
    if (parts[1]) cities.add(parts[1]);
  });
  return cities.size ? Array.from(cities).join(", ") : "--";
};

const countOvernights = (legs = []) =>
  legs.filter((leg) => leg.overnight).length;

const TYPE_LABELS = {
  flight: "Flight",
  sick: "Sick",
  rest: "Rest",
  lc: "Long call",
  sc: "Short call",
  training: "Training",
  dayoff: "Day off",
  golden: "Golden",
  ok: "OK",
  pay: "Pay",
  personal: "Personal",
  vacation: "Vacation",
  special: "Special",
};

export default function EventsPage() {
  const navigate = useNavigate();
  const [activeDate, setActiveDate] = useState(new Date());
  const [monthItems, setMonthItems] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [professionalDetails, setProfessionalDetails] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [manualTab, setManualTab] = useState("upload");
  const [manualUploading, setManualUploading] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [manualFile, setManualFile] = useState(null);
  const [manualForm, setManualForm] = useState({
    summary: "",
    start: "",
    end: "",
    location: "",
    description: "",
    start_timezone: "",
    end_timezone: "",
    sequence: "",
    calendar_name: "",
    calendar_color: "",
    creator_identity: "",
    creator_team_identity: "",
    created_at: "",
    last_modified: "",
  });

  const [options, setOptions] = useState({ calendars: [] });
  const [selectedCalendar, setSelectedCalendar] = useState(null);

  const loadMonth = async (date) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    try {
      const res = await api.get(`/api/calendar/events?year=${year}&month=${month}`);
      setMonthItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch calendar items:", err);
      setMonthItems([]);
    }
  };

  const selectDate = async (dateKey, focusId = null, focusEvent = null) => {
    try {
      const res = await api.get(`/api/calendar/events/date/${dateKey}`);
      const list = Array.isArray(res.data) ? res.data : [];
      setSelectedDate(dateKey);
      if (focusId) {
        const found = list.find((item) => String(item.id) === String(focusId));
        setSelectedItem(found || focusEvent || null);
      } else {
        setSelectedItem(list[0] || null);
      }
    } catch (err) {
      console.error("Failed to fetch date details:", err);
    }
  };

  useEffect(() => {
    loadMonth(activeDate);
  }, [activeDate]);

  const fetchProfessionalDetails = async () => {
    try {
      const res = await api.get("/user/professional-details");
      setProfessionalDetails(res.data);
    } catch {
      setProfessionalDetails(null);
    }
  };

  useEffect(() => {
    fetchProfessionalDetails();
  }, []);

  useEffect(() => {
    getCalendarSetup().then((res) => {
      const calendars = res.data?.options?.calendars || [];
      setOptions({ calendars });
      setSelectedCalendar(calendars[0] || null);
    });
  }, []);

  const getProviderType = () => {
    if (!professionalDetails) return null;
    if (professionalDetails.selected?.provider_type) {
      return professionalDetails.selected.provider_type;
    }
    const calendarName =
      professionalDetails.calendar_name ||
      professionalDetails.selected?.calendar_name ||
      "";
    const nameLC = calendarName.toLowerCase();
    if (nameLC.includes("google")) return "google";
    if (nameLC.includes("microsoft")) return "microsoft";
    if (nameLC.includes("manual")) return "manual";
    return null;
  };

  const handleManualInputChange = (e) => {
    const { name, value } = e.target;
    setManualForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleManualCreate = async () => {
    if (!manualForm.summary) {
      toast.warn("Title is required.");
      return;
    }

    try {
      setManualSaving(true);
      const uid = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `manual-${Date.now()}`;

      const startValue = manualForm.start || new Date().toISOString().slice(0, 16);
      const endValue =
        manualForm.end ||
        new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16);

      await api.post("/event/events", {
        uid,
        summary: manualForm.summary,
        start: startValue,
        end: endValue,
        location: manualForm.location,
        description: manualForm.description,
        start_timezone: manualForm.start_timezone,
        end_timezone: manualForm.end_timezone,
        sequence: manualForm.sequence,
        calendar_name: manualForm.calendar_name,
        calendar_color: manualForm.calendar_color,
        creator_identity: manualForm.creator_identity,
        creator_team_identity: manualForm.creator_team_identity,
        created_at: manualForm.created_at,
        last_modified: manualForm.last_modified,
        is_manual: true,
      });

      await loadMonth(activeDate);
      setShowAddModal(false);
      setManualForm({
        summary: "",
        start: "",
        end: "",
        location: "",
        description: "",
        start_timezone: "",
        end_timezone: "",
        sequence: "",
        calendar_name: "",
        calendar_color: "",
        creator_identity: "",
        creator_team_identity: "",
        created_at: "",
        last_modified: "",
      });
      toast.success("Event added successfully.");
    } catch (err) {
      console.error("Failed to add manual event:", err);
      toast.error("Failed to add event");
    } finally {
      setManualSaving(false);
    }
  };

  const handleManualUpload = async () => {
    if (!manualFile) {
      toast.warn("Please choose an .ics file.");
      return;
    }

    try {
      setManualUploading(true);
      const formData = new FormData();
      formData.append("file", manualFile);
      await api.post("/event/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await loadMonth(activeDate);
      setShowAddModal(false);
      setManualFile(null);
      toast.success("Calendar file uploaded successfully.");
    } catch (err) {
      console.error("Failed to upload calendar file:", err);
      toast.error("Failed to upload calendar file");
    } finally {
      setManualUploading(false);
    }
  };

  const providerType = getProviderType();

  const eventsWithMeta = useMemo(() => {
    return monthItems.map((item) => {
      const startTz = item.start_timezone || item.end_timezone;
      const endTz = item.end_timezone || item.start_timezone;
      return {
        ...item,
        startDate:
          normalizeDateFromString(item.start, startTz) || normalizeDate(item.start),
        endDate: normalizeDateFromString(item.end, endTz) || normalizeDate(item.end),
      };
    });
  }, [monthItems]);

  const barsByDateKey = useMemo(() => {
    const bars = {};
    const lanesByWeek = new Map();

    eventsWithMeta.forEach((ev) => {
      const eventStart = ev.startDate;
      const eventEnd = ev.endDate;

      let weekStart = startOfWeek(eventStart);
      const lastWeekStart = startOfWeek(eventEnd);

      while (weekStart.getTime() <= lastWeekStart.getTime()) {
        const weekKey = toDateKey(weekStart);
        const weekEnd = addDays(weekStart, 6);
        const seg = getBarSegments(ev, weekStart, weekEnd);
        if (!seg) {
          weekStart = addDays(weekStart, 7);
          continue;
        }

        if (!lanesByWeek.has(weekKey)) {
          lanesByWeek.set(weekKey, []);
        }
        const lanes = lanesByWeek.get(weekKey);

        let lane = -1;
        for (let l = 0; l < MAX_LANES; l += 1) {
          if (!lanes[l] || lanes[l] <= seg.col0) {
            lane = l;
            break;
          }
        }

        if (lane !== -1) {
          lanes[lane] = seg.col0 + seg.spanCols;
        }

        const labelPrefix = seg.isContinuation ? "‹ " : "";
        let dayCursor = new Date(seg.segStart);
        while (dayCursor <= seg.segEnd) {
          const dayKey = toDateKey(dayCursor);
          if (!bars[dayKey]) bars[dayKey] = [];
          const isFirst = isSameDay(eventStart, dayCursor);
          const isLast = isSameDay(eventEnd, dayCursor);
          const isWeekStart = dayCursor.getDay() === 0;
          const showLabel = isFirst || isWeekStart;
          let shape = "mid";
          if (isFirst && isLast) shape = "solo";
          else if (isFirst) shape = "start";
          else if (isLast) shape = "end";

          bars[dayKey].push({
            event: ev,
            lane,
            spanCols: 1,
            showLabel,
            shape,
            label: showLabel ? `${labelPrefix}${ev.label}` : "",
          });
          dayCursor = addDays(dayCursor, 1);
        }

        weekStart = addDays(weekStart, 7);
      }
    });

    Object.keys(bars).forEach((key) => {
      bars[key] = bars[key].sort((a, b) => {
        if (a.lane === -1 && b.lane !== -1) return 1;
        if (a.lane !== -1 && b.lane === -1) return -1;
        if (a.lane === b.lane) return 0;
        return a.lane - b.lane;
      });
    });

    return bars;
  }, [eventsWithMeta]);

  return (
    <main className="dashboard-screen">
      <div className="container-fluid">
        <div className="row gx-5">
          <div className="col-12 col-lg-3 left-sidebar-main">
            <LeftSidebar />
          </div>
          <div className="col-12 col-lg-9">
            <Header />
            <div className="row">
              <div className="event-page">
                <div className="event-title-sec">
                  <h1 className="mb-0">Event Calendar</h1>
                  <div>
                    {providerType === "manual" && (
                      <button className="btn5" onClick={() => setShowAddModal(true)}>
                        <i className="bi bi-plus"></i> Add Event
                      </button>
                    )}
                  </div>
                </div>

                <div className="event-calendar mt-5">
                  <Calendar
                    onActiveStartDateChange={({ activeStartDate }) => {
                      setActiveDate(activeStartDate);
                    }}
                    tileClassName={({ date, view }) => {
                      if (view !== "month") return null;
                      const key = toDateKey(date);
                      return (barsByDateKey[key] && barsByDateKey[key].length) ? "has-bars" : null;
                    }}
                    onClickDay={(date) => {
                      const key = toDateKey(date);
                      selectDate(key);
                    }}
                    tileContent={({ date, view }) => {
                      if (view !== "month") return null;

                      const dateKey = toDateKey(date);
                      const bars = barsByDateKey[dateKey] || [];
                      if (!bars.length) return null;

                      const maxLane = bars.reduce(
                        (max, bar) => (bar.lane >= 0 ? Math.max(max, bar.lane) : max),
                        -1
                      );
                      const barAreaHeight =
                        maxLane >= 0 ? (maxLane + 1) * (BAR_HEIGHT + BAR_GAP) : 0;

                      return (
                        <div className="calendar-bar-stack">
                          <div
                            className="calendar-bar-layer"
                            style={{ height: `${barAreaHeight}px` }}
                          >
                            {bars
                              .filter((bar) => bar.lane >= 0)
                              .map((bar) => (
                                <div
                                  key={`${bar.event.id}-${bar.spanCols}-${bar.lane}`}
                                  className={`calendar-bar ${bar.shape} cal-type-${bar.event.type}`}
                                  style={{
                                    top: `${bar.lane * (BAR_HEIGHT + BAR_GAP)}px`,
                                    left: "0px",
                                    width: "100%",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    selectDate(dateKey, bar.event.id, bar.event);
                                  }}
                                >
                                  {bar.showLabel && (
                                    <span className="calendar-bar__label">{bar.label}</span>
                                  )}
                                </div>
                              ))}
                          </div>
                        </div>
                      );
                    }}
                  />
                </div>
              </div>

              {selectedItem && (
                <div className="modal fade show d-block calendar-modal">
                  <div className="modal-dialog modal-lg modal-dialog-centered">
                    <div className="modal-content event-details-modal">
                      <div className="modal-header border-0">
                        <h5 className="modal-title title2">Event Details</h5>
                        <button
                          className="btn-close btn-close-white"
                          onClick={() => setSelectedItem(null)}
                        />
                      </div>
                      <div className="modal-body">
                        <div className="event-section-title">
                          {formatLongDate(selectedDate)}
                        </div>

                        {selectedItem.rotation ? (
                          <>
                            <div className="event-section-title">Rotation Summary</div>
                            <div className="rotation-summary-card">
                              <span className="left-indicator" />
                              {selectedItem.rotation.event_summary || selectedItem.label || "Rotation Summary"}
                            </div>

                            <div className="info-block">
                              <label>Start</label>
                              <div className="info-value">
                                  {formatDateTime(
                                    selectedItem.rotation.start_datetime,
                                    selectedItem.rotation.start_timezone
                                  )}
                              </div>
                            </div>
                            <div className="info-block">
                              <label>End</label>
                              <div className="info-value">
                                  {formatDateTime(
                                    selectedItem.rotation.end_datetime,
                                    selectedItem.rotation.end_timezone
                                  )}
                              </div>
                            </div>
                            <div className="info-block">
                              <label>Cities Involved</label>
                              <div className="info-value">
                                {extractCitiesFromLegs(selectedItem.rotation.legs || [])}
                              </div>
                            </div>
                            <div className="info-row">
                              <div className="info-block">
                                <label>Duty Days</label>
                                <div className="info-value">
                                  {selectedItem.rotation.days || countDaysBetween(selectedItem.start, selectedItem.end)}
                                </div>
                              </div>
                              <div className="info-block">
                                <label>Overnights</label>
                                <div className="info-value">
                                  {countOvernights(selectedItem.rotation.legs || [])}
                                </div>
                              </div>
                            </div>
{selectedItem.rotation.event_uid && (
                              <div className="mt-4">
                                <button
                                  className="btn5"
                                  onClick={() => navigate(`/rotation/${selectedItem.rotation.event_uid}`)}
                                >
                                  View
                                </button>
                              </div>
                            )}

                          </>
                        ) : (
                          <>
                            <div className="rotation-summary-card">
                              <span className="left-indicator" />
                              {selectedItem.label}
                            </div>
                            <div className="badge-row">
                              <span className="detail-badge">
                                {TYPE_LABELS[selectedItem.type] || selectedItem.type}
                              </span>
                            </div>
                            <div className="info-block">
                              <label>Start</label>
                              <div className="info-value">
                                {formatDateTimeWithTz(
                                  selectedItem.start,
                                  selectedItem.start_timezone || selectedItem.end_timezone
                                )}
                              </div>
                            </div>
                            <div className="info-block">
                              <label>End</label>
                              <div className="info-value">
                                {formatDateTimeWithTz(
                                  selectedItem.end,
                                  selectedItem.end_timezone || selectedItem.start_timezone
                                )}
                              </div>
                            </div>
                            <div className="info-block">
                              <label>Details</label>
                              <div className="info-value">
                                {selectedItem.description || "No description"}
                              </div>
                            </div>
                            {selectedItem.event_uid && (
                              <div className="mt-4">
                                <button
                                  className="btn5"
                                  onClick={() => navigate(`/rotation/${selectedItem.event_uid}`)}
                                >
                                  View
                                </button>
                              </div>
                            )}

                          </>
                        )}
</div>
                    </div>
                  </div>
                </div>
              )}

              {showAddModal && providerType === "manual" && (
                <div className="modal add-event-modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                  <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5><img src="images/add-event.svg" className="img-fluid"/> Add Event</h5>
                        <button className="btn-close" onClick={() => setShowAddModal(false)} />
                      </div>
                      <div className="modal-body">
                        {manualTab === "create" && (
                          <div className="row g-3">
                            <div className="col-12">
                              <label className="form-label">Title *</label>
                              <input
                                type="text"
                                className="form-control"
                                name="summary"
                                value={manualForm.summary}
                                onChange={handleManualInputChange}
                                placeholder="Event title"
                              />
                            </div>
                            <div className="col-6">
                              <label className="form-label">Start</label>
                              <input
                                type="datetime-local"
                                className="form-control"
                                name="start"
                                value={manualForm.start}
                                onChange={handleManualInputChange}
                              />
                            </div>
                            <div className="col-6">
                              <label className="form-label">End</label>
                              <input
                                type="datetime-local"
                                className="form-control"
                                name="end"
                                value={manualForm.end}
                                onChange={handleManualInputChange}
                              />
                            </div>
                            <div className="col-6">
                              <label className="form-label">Start Timezone</label>
                              <input
                                type="text"
                                className="form-control"
                                name="start_timezone"
                                value={manualForm.start_timezone}
                                onChange={handleManualInputChange}
                                placeholder="e.g., UTC"
                              />
                            </div>
                            <div className="col-6">
                              <label className="form-label">End Timezone</label>
                              <input
                                type="text"
                                className="form-control"
                                name="end_timezone"
                                value={manualForm.end_timezone}
                                onChange={handleManualInputChange}
                                placeholder="e.g., UTC"
                              />
                            </div>
                            <div className="col-12">
                              <label className="form-label">Location</label>
                              <input
                                type="text"
                                className="form-control"
                                name="location"
                                value={manualForm.location}
                                onChange={handleManualInputChange}
                                placeholder="Location (optional)"
                              />
                            </div>
                            <div className="col-12">
                              <label className="form-label">Description</label>
                              <textarea
                                className="form-control"
                                name="description"
                                value={manualForm.description}
                                onChange={handleManualInputChange}
                                rows="3"
                                placeholder="Description (optional)"
                              />
                            </div>
                            <div className="col-6">
                              <label className="form-label">Sequence</label>
                              <input
                                type="number"
                                className="form-control"
                                name="sequence"
                                value={manualForm.sequence}
                                onChange={handleManualInputChange}
                                placeholder="0"
                              />
                            </div>
                            <div className="col-6">
                              <label className="form-label">Calendar Name</label>
                              <input
                                type="text"
                                className="form-control"
                                name="calendar_name"
                                value={manualForm.calendar_name}
                                onChange={handleManualInputChange}
                                placeholder="Manual Calendar"
                              />
                            </div>
                            <div className="col-6">
                              <label className="form-label">Calendar Color</label>
                              <input
                                type="text"
                                className="form-control"
                                name="calendar_color"
                                value={manualForm.calendar_color}
                                onChange={handleManualInputChange}
                                placeholder="#ff0000"
                              />
                            </div>
                            <div className="col-6">
                              <label className="form-label">Creator Identity</label>
                              <input
                                type="text"
                                className="form-control"
                                name="creator_identity"
                                value={manualForm.creator_identity}
                                onChange={handleManualInputChange}
                                placeholder="creator@example.com"
                              />
                            </div>
                            <div className="col-6">
                              <label className="form-label">Creator Team Identity</label>
                              <input
                                type="text"
                                className="form-control"
                                name="creator_team_identity"
                                value={manualForm.creator_team_identity}
                                onChange={handleManualInputChange}
                                placeholder="team"
                              />
                            </div>
                            <div className="col-6">
                              <label className="form-label">Created At</label>
                              <input
                                type="datetime-local"
                                className="form-control"
                                name="created_at"
                                value={manualForm.created_at}
                                onChange={handleManualInputChange}
                              />
                            </div>
                            <div className="col-6">
                              <label className="form-label">Last Modified</label>
                              <input
                                type="datetime-local"
                                className="form-control"
                                name="last_modified"
                                value={manualForm.last_modified}
                                onChange={handleManualInputChange}
                              />
                            </div>
                            <div className="col-12">
                              <button
                                className="btn btn-success w-100"
                                onClick={handleManualCreate}
                                disabled={manualSaving}
                              >
                                {manualSaving ? "Saving..." : "Save Event"}
                              </button>
                            </div>
                          </div>
                        )}

                        {manualTab === "upload" && (
                          <div>
                            <p className="text-muted small">
                              Upload an .ics file to add events manually.
                            </p>
                            <input
                              type="file"
                              accept=".ics,text/calendar"
                              className="form-control mb-3"
                              onChange={(e) => setManualFile(e.target.files?.[0] || null)}
                            />
                            <button
                              className="btn5 mt-3"
                              onClick={handleManualUpload}
                              disabled={manualUploading}
                            >
                              {manualUploading ? "Uploading..." : "Upload ICS File"}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
